import React, { useState, useEffect } from 'react';
import { Container, Header, Segment, Accordion, Icon, List, Divider, Button, Modal } from 'semantic-ui-react';
import '@styles/BlogBody.css';
import '@styles/GeneralTheme.css';
import WebToLead from '@components/WebToLead';

const TechnicalExpertise = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Add Open Graph meta tags for technical expertise page
  useEffect(() => {
    const updateMetaTag = (property, content, isProperty = true) => {
      const attribute = isProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(attribute, property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };

    document.title = 'Technical Expertise - Cloud CZR | Salesforce Development & Technology Consulting';
    updateMetaTag('og:title', 'Technical Expertise - Cloud CZR | Salesforce Development & Technology Consulting');
    updateMetaTag('og:description', 'Explore the technical skills, certifications, and expertise in Salesforce development, integrations, and technology consulting.');
    updateMetaTag('og:url', window.location.href);
    updateMetaTag('og:type', 'website');
    updateMetaTag('og:image', `${window.location.origin}/assets/CloudChiRho.png`);
    updateMetaTag('og:image:alt', 'Cloud CZR, Cloudczr, cloudczr.com');
    
    updateMetaTag('description', 'Explore the technical skills, certifications, and expertise in Salesforce development, integrations, and technology consulting.', false);
    updateMetaTag('twitter:title', 'Technical Expertise - Cloud CZR | Salesforce Development & Technology Consulting', false);
    updateMetaTag('twitter:description', 'Explore the technical skills, certifications, and expertise in Salesforce development, integrations, and technology consulting.', false);
  }, []);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleAccordionClick = (e, { index }) => {
    setActiveIndex(activeIndex === index ? -1 : index);
  };

  const expertiseCategories = [
    {
      title: 'Certifications',
      description: 'Demonstrate proven expertise through industry-recognized credentials that ensure reliable, standards-compliant solutions.',
      skills: [
        'Salesforce Certified Platform Developer 1',
        'Salesforce Certified Platform Developer 2',
        'Salesforce Certified Agentforce Specialist',
        'Coursera Google Project Management Certification'
      ],
      examples: [
        'Achieved Salesforce certifications to deliver compliant, high-quality platform solutions.',
        'Applied Google Project Management certification to streamline development processes and meet project deadlines.'
      ]
    },
    {
      title: 'Salesforce Development & Design',
      description: 'Build custom interfaces and automate workflows that perfectly fit your unique business processes and drive efficiency.',
      skills: [
        'Apex (Debugging)',
        'User Interface (Lightning Web Components, Lightning Aura Framework, Visualforce)',
        'Configurations (Custom Metadata, Custom Settings)',
        'Salesforce Flows (Invocable Apex, Screen Flows, Record Triggered Flows)',
        'Events (Change Data Capture, Platform Events)',
        'Specialized Tools & Platforms (nCino, Fusion Risk Management, Cloud Theory Deal Engine, Account Engagement, Five9)'
      ],
      examples: [
        'Developed Custom Lightning Web Components (LWC) to match sales workflow at Coastal Debt Resolve.',
        'Implemented Salesforce platform encryption and configured metadata for a risk management solution at Demand Chain Systems.',
        'Configured Cloud Theory Deal Engine\'s Private Equity Managed Package and analyzed metadata for migration.',
        'Developed a dynamic Aura component UI for nCino\'s Document Manager, proposed an Apex trigger framework, and built a proof-of-concept early warning alert system at Accenture.'
      ]
    },
    {
      title: 'Agentforce',
      description: 'Automate complex workflows with AI-powered agents that learn and adapt to your business needs.',
      skills: [
        'Prompt Templates',
        'Custom Agents',
        'Custom Agent Actions'
      ],
      examples: [
        'Built a Screen Flow in Flow Builder to operationalize Agentforce prompt templates.',
        'Integrated Custom Prompt Template with Salesforce Lead object.',
        'Optimized Agentforce implementation by testing and refining prompt templates.',
        'Developed and Optimized an Agentforce Solution for GitHub Deployments.'
      ]
    },
    {
      title: 'Programming Languages & Frameworks',
      description: 'Leverage modern programming techniques to create scalable, maintainable code that powers your applications.',
      skills: [
        'Enterprise Design Patterns',
        'Apex',
        'Java',
        'JavaScript',
        'TypeScript',
        'Node.js',
        'React'
      ],
      examples: [
        'Developed and Optimized an Agentforce Solution for GitHub Deployments using Apex and JavaScript.',
        'Developed a static website hosted on GitHub and built robust microservices with Node.js and React.'
      ]
    },
    {
      title: 'Integration & API Development',
      description: 'Connect systems seamlessly to create unified workflows and eliminate data silos across your organization.',
      skills: [
        'REST',
        'SOAP',
        'Vercel',
        'Heroku'
      ],
      examples: [
        'Managed API connections with 3rd party services and production issues in real time.',
        'Lead integration from Salesforce with a legacy CRM system at Multi-Bank Securities.',
        'Engineered a Bidirectional GitHub-Salesforce Integration.'
      ]
    },
    {
      title: 'Version Control & DevOps',
      description: 'Implement reliable deployment processes that ensure code quality and rapid, safe releases.',
      skills: [
        'Git',
        'SFDX/SF CLI',
        'Gearset',
        'Copado',
        'Force.com Migration Tool',
        'CI/CD'
      ],
      examples: [
        'Engineered a Bidirectional GitHub-Salesforce Integration for seamless deployments.',
        'Refactored a Salesforce managed package for club management using Git and CI/CD practices at Foretees.'
      ]
    },
    {
      title: 'Data Modeling',
      description: 'Structure and transform data efficiently to support accurate reporting and informed decision-making.',
      skills: [
        'Transformation',
        'ETL'
      ],
      examples: [
        'Designed and developed scalable Salesforce solutions with effective data modeling.',
        'Implemented ETL processes for data transformation in various Salesforce implementations.'
      ]
    },
    {
      title: 'Advertising',
      description: 'Drive targeted traffic and conversions through strategic digital advertising campaigns.',
      skills: [
        'Google Ads',
        'X Ads',
        'QR Code tracking'
      ],
      examples: [
        'Implemented Salesforce Account Engagement (Pardot) solutions for email and SMS campaigns at Coastal Debt Resolve (2025-2026).'
      ]
    },
    {
      title: 'Web Analytics',
      description: 'Track user behavior and campaign performance to optimize your digital presence and ROI.',
      skills: [
        'Matomo',
        'Google Tags'
      ],
      examples: [
        'Configured Google Tags and Matomo for comprehensive web analytics tracking.'
      ]
    },
    {
      title: '3D Printing',
      description: 'Create physical prototypes and custom parts to bring ideas to life and solve practical challenges.',
      skills: [
        'CAD Design',
        'Repairing machine'
      ],
      examples: [
        'Utilized CAD Design and 3D printing for prototyping custom solutions.'
      ]
    },
    {
      title: 'Project Management',
      description: 'Deliver projects on time and within scope using proven methodologies and collaborative practices.',
      skills: [
        'SDLC',
        'Agile (Scrum, Kanban)',
        'Waterfall',
        'Sprint Planning',
        'User Stories',
        'Test-Driven Development'
      ],
      examples: [
        'Applied Agile methodologies and Sprint Planning to manage Salesforce development projects.',
        'Used Test-Driven Development to ensure quality in Apex and JavaScript implementations.'
      ]
    },
    {
      title: 'Soft Skills',
      description: 'Communicate effectively, negotiate successfully, and lead teams to achieve outstanding results.',
      skills: [
        'Social Engineering defence (Assertive Redirection, Verification Protocols, Psychological Resilience)',
        'Persuasion',
        'Effective Communication',
        'Negotiation'
      ],
      examples: [
        'Achieved Compliance Goals through effective communication and negotiation skills.',
        'Applied persuasion and effective communication in client interactions and project leadership.'
      ]
    },
    {
      title: 'Discussion Platform',
      description: 'It\'s a platform for discussing ways to streamline your technology stack and cultivate a productive mindset.',
      skills: [],
      examples: []
    }
  ];

  return (
    <Container fluid style={{ marginTop: '2em', padding: '0 2em', maxWidth: 'none !important', width: '100% !important' }}>
      <div style={{ textAlign: 'center', marginBottom: '2em', backgroundColor: 'white', padding: '1em', borderRadius: '8px' }}>
        <img 
          src={`${process.env.PUBLIC_URL}/assets/CZRStylePermissions.svg`}
          alt="Cloud CZR Style Permissions" 
          style={{ maxWidth: '100%', height: 'auto' }}
        />
      </div>
      <Header as='h1' textAlign='center' style={{ color: '#a9885c' }}>
        Technical Expertise
      </Header>

      <Divider hidden />

      <Segment padded style={{ width: '100%', maxWidth: 'none' }}>
        
        <Accordion fluid styled>
          {expertiseCategories.map((category, index) => (
            <div key={index}>
              <Accordion.Title
                active={activeIndex === index}
                index={index}
                onClick={handleAccordionClick}
                style={{ 
                  backgroundColor: activeIndex === index ? '#702963' : '#f9f9fa',
                  color: activeIndex === index ? '#a9885c' : '#702963',
                  padding: '1em',
                  fontSize: '1.2em',
                  fontWeight: 'bold'
                }}
              >
                <Icon name='dropdown' />
                {category.title}
              </Accordion.Title>
              <Accordion.Content active={activeIndex === index} style={{ padding: '1em', backgroundColor: '#f9f9fa' }}>
                <p style={{ color: '#a9885c', fontSize: '1.1em', fontWeight: 'bold', marginBottom: '1em' }}>
                  {category.description}
                </p>
                
                <Header as='h4' style={{ color: '#702963' }}>Skills & Tools</Header>
                <List bulleted style={{ color: '#a9885c', marginBottom: '1em' }}>
                  {category.skills.map((skill, skillIndex) => (
                    <List.Item key={skillIndex}>{skill}</List.Item>
                  ))}
                </List>
                
                <Header as='h4' style={{ color: '#702963' }}>Real-World Examples</Header>
                <List bulleted style={{ color: '#a9885c' }}>
                  {category.examples.map((example, exampleIndex) => (
                    <List.Item key={exampleIndex}>{example}</List.Item>
                  ))}
                </List>
              </Accordion.Content>
            </div>
          ))}
        </Accordion>
      </Segment>

      <Divider hidden />

      <Segment padded textAlign='center' style={{ width: '100%', maxWidth: 'none' }}>
        <Header as='h2' style={{ color: '#a9885c' }}>Ready to bring this expertise to your project?</Header>
        <p style={{ color: '#a9885c', fontSize: '1.2em', marginBottom: '2em' }}>
          Let's discuss how these skills can transform your business challenges into opportunities.
        </p>
        <Button primary size='large' className="custom-button-body" onClick={handleOpenModal}>Get In Touch</Button>
      </Segment>

      {/* Contact Form Modal */}
      <Modal open={modalOpen} onClose={handleCloseModal} size='large' style={{ backgroundColor: 'white' }}>
        <Modal.Header style={{ backgroundColor: '#702963', color: '#a9885c', textAlign: 'center' }}>
          <Icon name='envelope' />
          Contact Us
        </Modal.Header>
        <Modal.Content style={{ backgroundColor: 'white' }}>
          <WebToLead />
        </Modal.Content>
        <Modal.Actions style={{ backgroundColor: '#f9f9f9', textAlign: 'center' }}>
          <Button 
            onClick={handleCloseModal}
            style={{ backgroundColor: '#702963', color: '#a9885c' }}
          >
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default TechnicalExpertise;